import React, { useRef, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import toJalali from "../utils/Time";
import User from "../assets/images/Frame 440.png";
import razeghiImg from "../assets/images/Restaurant_Rasht_Razeghi_1.webp";
import { Link } from "react-router-dom";
import CommentSkeleton from "./CommentSkeleton";
import useUserStore from "../store/userStore";

interface Review {
  id: string | number;
  name: string;
  date: string;
  rate: number;
  comment: string;
  place: string;
  img: string;
  placeId: string;
}

const UserComments: React.FC = () => {
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { user, clearUser } = useUserStore();

  const scrollDesktopNext = () =>
    desktopScrollRef.current?.scrollBy({ left: -600, behavior: "smooth" });
  const scrollDesktopPrev = () =>
    desktopScrollRef.current?.scrollBy({ left: 600, behavior: "smooth" });

  useEffect(() => {
    const fetchReviewsAndPlaces = async () => {
      setLoading(true);
      try {
        const reviewsResponse = await fetch("https://lootkatrip.ir/api/reviews/?shuffle=true&page_size=10");

        if (!reviewsResponse.ok) throw new Error("Failed to fetch reviews");

        const reviewsData = await reviewsResponse.json();
        const reviewsList = reviewsData.results || [];

        // اصلاح شده: دسترسی به آرایه نظرات از طریق فیلد results
        const reviewsWithImages = await Promise.all(
          reviewsList.map(async (r: any) => {
            // *** تغییر اصلی: بررسی امتیاز ***
            if (r.rate < 4) {
              return null; // اگر امتیاز کمتر از ۴ است، این آیتم را حذف کن (null برگردان)
            }

            let placeImg = razeghiImg;
            try {
              const placeResponse = await fetch(`https://lootkatrip.ir/api/places/${r.place}/`);
              if (placeResponse.ok) {
                const placeData = await placeResponse.json();
                if (placeData.images && placeData.images.length > 0) {
                  placeImg = `https://lootkatrip.ir${placeData.images[0].file_url}`;
                }
              }
            } catch (err) {
              console.error(`Failed to fetch place for review ${r.review_id}:`, err);
            }
            return {
              id: r.review_id || Math.random().toString(),
              name: r.user_name === "Unknown" ? "کاربر سایت" : (r.user_name || "کاربر"),
              date: r.date || "",
              rate: r.rate || 0,
              comment: r.comment || "کاربر تجربه خود را ثبت کرده است.",
              placeId: r.place || "",
              place: r.place_name || "مکان نامشخص",
              img: placeImg,
            };
          })
        );

        // حذف مقادیر null (نظراتی که امتیازشان کمتر از ۴ بود) از آرایه نهایی
        const filteredReviews = reviewsWithImages.filter((r) => r !== null) as Review[];
        
        setReviews(filteredReviews);
      } catch (err) {
        console.error("Failed to fetch reviews:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviewsAndPlaces();
  }, []);

  const renderSkeletons = () => {
    return Array.from({ length: 4 }).map((_, index) => (
      <CommentSkeleton key={`skeleton-${index}`} />
    ));
  };

  return (
    <section className="py-6 px-0 desktop:px-16">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 desktop:px-20 tablet:px-20 px-[16px]">
        <h2 className="text-lg tablet:text-2xl desktop:text-3xl font-myYekanDemibold text-center">
          نظرات کاربران
        </h2>
      </div>
      {/* Mobile view */}
      <div className="md:hidden relative font-myYekanFaNumRegular">
        <div className="px-0 relative">
          <div
            ref={mobileScrollRef}
            className="flex overflow-x-auto pb-6 space-x-2 rtl:space-x-reverse scroll-smooth px-2 scrollbar-hide"
          >
            {loading ? (
              renderSkeletons()
            ) : (
              reviews.map((review) => (
                <div
                  key={review.id}
                  className="min-w-[313px] w-[343px] h-[250px] rounded-2xl border border-gray-200 p-[16px] flex flex-col justify-between shadow-sm bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src={User}
                        alt="User avatar"
                        className="w-[54px] h-[54px] rounded-full object-cover"
                      />
                      <div className="text-[14px] flex flex-row items-center gap-[8px]">
                        <p className="text-[#222222]/80">{review.name}</p>
                        <p className="text-[#222222]/50 text-xs">{toJalali(review.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 text-[#1BA75E] fill-[#1BA75E]" />
                      <span className="text-[#222222]/50">{review.rate}</span>
                    </div>
                  </div>
                  <p className="text-[14px] font-myYekanRegular text-justify text-[#222222]/80 flex-1 leading-6 pt-[20px] line-clamp-4">
                    {review.comment}
                  </p>
                  <div className="flex items-center justify-between mt-4">
                    <Link to={`/places/${review.placeId}`}>
                      <div className="flex items-center gap-2">
                        {/* {review.img && (
                          <img
                            src={review.img}
                            alt={review.place}
                            className="w-6 h-6 rounded rounded-full object-cover"
                          />
                        )} */}
                        <span className="text-sm text-[#222222]/80 font-myYekanFaNumDemiBold">
                          {review.place}
                        </span>
                      </div>
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Desktop view */}
      <div className="hidden md:block relative px-12 font-myYekanFaNumRegular">
        <div className="relative flex items-center">
          <button
            onClick={scrollDesktopPrev}
            className="z-10 w-10 h-10 flex items-center justify-center"
            aria-label="prev reviews"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
          <div
            ref={desktopScrollRef}
            className="flex overflow-x-auto pb-6 scrollbar-hide space-x-6 rtl:space-x-reverse scroll-smooth flex-1 px-2"
          >
            {loading ? (
              renderSkeletons()
            ) : (
              reviews.map((review) => (
                <div
                  key={review.id}
                  className="min-w-[343px] w-[343px] h-[252px] rounded-2xl border border-gray-200 p-[16px] flex flex-col justify-between shadow-sm bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src={User}
                        alt="User avatar"
                        className="w-[54px] h-[54px] rounded-full object-cover"
                      />
                      <div className="text-[14px] flex flex-row items-center gap-[8px]">
                        <p className="text-[#222222]/80">{review.name}</p>
                        <p className="text-[#222222]/50 text-xs">{toJalali(review.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 text-[#1BA75E] fill-[#1BA75E]" />
                      <span className="text-[#222222]/50">{review.rate}</span>
                    </div>
                  </div>
                  <p className="text-[14px] text-[#222222]/80 text-justify flex-1 leading-6 pt-[24px] line-clamp-4">
                    {review.comment}
                  </p>
                  <div className="flex justify-center gap-1 mt-6">
                    {/* <span>درباره</span> */}
                    <span></span>
                    <Link to={`/places/${review.placeId}`}>

                        {/* {review.img && (
                          <img
                            src={review.img}
                            alt={review.place}
                            className="w-10 h-10 rounded rounded-full object-cover"
                          />
                        )} */}
                        <span className="text-[14px] text-[#222222]/80 font-myYekanFaNumDemiBold">
                         {review.place}
                        </span>

                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
          <button
            onClick={scrollDesktopNext}
            className="z-10 w-10 h-10 flex items-center justify-center"
            aria-label="next reviews"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Hide scrollbar styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default UserComments;